import Model from "./Model";
import AppRole from "./AppRole.ts";
import {useCookies} from "../composables/useCookies.ts";
import {cleanQuery} from "../utils";
import {UserType} from "../types/UserType.ts";
import {PaginatedData} from "../types/PaginatedData.ts";
import {IUserStatus} from "../types/IUserStatus.ts";
import api from "../api.ts";

/**
 * Represents a user in the application.
 * Extends the base Model class and includes properties, methods,
 * and utilities related to the user.
 *
 * @author Jackson Bingham <jackson@onlineimage.com>
 * @date November 2024
 */
class User extends Model {
    // Private properties for user attributes
    private _username: string;
    private _first_name: string;
    private _last_name: string;
    private _partner_id: string;
    private _user_icon: string;
    private _email: string;
    private _phone: string;
    private _created_at: string;
    private _last_login: string | null;
    private _isAdmin: 0 | 1;
    private _roles: AppRole[];
    private _type: UserType | undefined;
    private _access_controls: any[] | undefined;
    private _user_status: IUserStatus;
    private _two_factor_enabled: boolean;
    private _updated_at: string | null;

    public static async fetch({
                                  page,
                                  itemsPerPage,
                                  sortBy,
                                  search
                              }): Promise<[boolean, PaginatedData<User>, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const cleanedQuery: string = cleanQuery(search || '');
            const response = await api.get(
                `/api/v4/users?page=${page}&limit=${itemsPerPage}&sort=${sortBy}&search=${cleanedQuery}`,
                {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${tokenCookie}`,
                    }
                });

            const rawData = response.data;

            const data: PaginatedData<User> = {
                ...rawData,
                data: rawData.data.map((user: any) => User.fromJson(user)), // Construct each user into an object
            };

            return [true, data]
        } catch (error) {
            return [false, null, error];
        }
    }

    public static async create(values: IUserForm): Promise<[boolean, User, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.post(`/api/v4/users`, values, {
                withCredentials: true,
                headers: {
                    Authorization: `Bearer ${tokenCookie}`,
                }
            })

            const newUser: User = User.fromJson(response.data.user);
            return [true, newUser];
        } catch (error) {
            return [false, null, error];
        }
    }

    /**
     * Creates a new immutable User instance from a JSON object.
     * @param json - The JSON object representing the user.
     * @returns A readonly User instance.
     * @example
     * const userJson = { id: "1", username: "jdoe", firstName: "John", ... };
     * const user = User.fromJson(userJson);
     * console.log(user.fullName); // "John Doe"
     */
    public static fromJson(json: any): User {
        return new User(
            json.id,
            json.username,
            json.first_name,
            json.last_name,
            json.partner_id,
            json.user_icon,
            json.email,
            json.phone,
            json.created_at,
            json.last_login,
            json.isAdmin,
            json.roles,
            json.user_status,
            json.two_factor_enabled,
            json.updated_at,
            json?.type,
            json?.access_controls,
        );
    }

    public static avatarColor(last_name: string): string {
        const avatarColorMap: { [key: string]: string } = {
            a: "red",
            b: "red",
            c: "red",
            d: "red",
            e: "blue",
            f: "blue",
            g: "blue",
            h: "blue",
            i: "green",
            j: "green",
            k: "green",
            l: "green",
            m: "indigo",
            n: "indigo",
            o: "indigo",
            p: "indigo",
            q: "teal",
            r: "teal",
            s: "teal",
            t: "teal",
            u: "pink",
            v: "pink",
            w: "pink",
            x: "pink",
            y: "purple",
            z: "purple",
        };

        const firstLetter = last_name[0].toLowerCase();
        return avatarColorMap[firstLetter] || "blue-grey";
    }

    /**
     * Constructs a new User instance.
     * @param id - The unique identifier for the user.
     * @param username - The username of the user.
     * @param first_name - The first name of the user.
     * @param last_name - The last name of the user.
     * @param partner_id - The partner ID associated with the user.
     * @param user_icon - The icon CallbackURL for the user's profile.
     * @param email - The user's email address.
     * @param phone - The user's phone number.
     * @param created_at - The creation date of the user's account.
     * @param last_login - The last login date of the user (nullable).
     * @param isAdmin - Whether the user has administrative privileges.
     * @param roles - The roles associated with the user.
     * @param user_status
     * @param two_factor_enabled
     * @param type
     * @param updated_at
     * @param access_controls
     * @example
     * const user = new User(
     *   "123",
     *   "jdoe",
     *   "John",
     *   "Doe",
     *   "partner-456",
     *   "/images/avatar.png",
     *   "john.doe@example.com",
     *   "123-456-7890",
     *   "2024-01-01T00:00:00Z",
     *   "2024-01-02T12:00:00Z",
     *   true,
     *   [{ id: "1", title: "Admin", permissions: [...] }]
     * );
     */
    constructor(
        id: string,
        username: string,
        first_name: string,
        last_name: string,
        partner_id: string,
        user_icon: string,
        email: string,
        phone: string,
        created_at: string,
        last_login: string | null,
        isAdmin: 0 | 1,
        roles: any[],
        user_status: IUserStatus,
        two_factor_enabled: boolean,
        updated_at: string,
        type?: UserType | undefined,
        access_controls?: any[] | undefined,
    ) {
        super(id); // Call the base class constructor with the user ID
        this._username = username;
        this._first_name = first_name;
        this._last_name = last_name;
        this._partner_id = partner_id;
        this._user_icon = user_icon;
        this._email = email;
        this._phone = phone;
        this._created_at = created_at;
        this._last_login = last_login;
        this._isAdmin = isAdmin;
        this._roles = roles?.map(
            (role) => new AppRole(role.id, role.title, role.permissions, role.users)
        );
        this._user_status = user_status;
        this._two_factor_enabled = two_factor_enabled;
        this._updated_at = updated_at;
        this._type = type;
        this._access_controls = access_controls;
    }

    // Getters for user properties

    /** Gets the username of the user. */
    public get username(): string {
        return this._username;
    }

    /** Gets the first name of the user. */
    public get first_name(): string {
        return this._first_name;
    }

    /** Gets the last name of the user. */
    public get last_name(): string {
        return this._last_name;
    }

    /** Gets the partner ID associated with the user. */
    public get partner_id(): string {
        return this._partner_id;
    }

    /** Gets the user icon CallbackURL with a cache-busting query param. */
    public get user_icon(): string {
        return this._user_icon ? `${this._user_icon}?t=${this.updated_at}` : null;
    }

    /** Gets the user's email address. */
    public get email(): string {
        return this._email;
    }

    /** Gets the user's phone number. */
    public get phone(): string {
        return this._phone;
    }

    /** Gets the account creation date as a `Date` object. */
    public get created_at(): Date | null {
        return this._created_at ? new Date(this._created_at) : null;
    }

    /** Gets the last login date as a `Date` object, or null if not available. */
    public get last_login(): Date | null {
        return this._last_login ? new Date(this._last_login) : null;
    }

    /** Gets whether the user has administrative privileges. */
    public get isAdmin(): 0 | 1 {
        return this._isAdmin;
    }

    /** Gets the last updated date */
    public get updated_at(): Date | null {
        return this._updated_at ? new Date(this._updated_at) : null;
    }

    /** Gets the user's full name (first name and last name concatenated). */
    public get fullName(): string {
        return `${this._first_name} ${this._last_name}`;
    }

    /** Gets the user's initials based on their first and last names. */
    public get initials(): string {
        return `${this._first_name.charAt(0)}${this._last_name.charAt(0)}`;
    }

    /** Gets the roles assigned to the user. */
    public get roles(): AppRole[] {
        return this._roles;
    }

    public get user_status(): IUserStatus {
        return this._user_status;
    }

    public get two_factor_enabled(): boolean {
        return this._two_factor_enabled;
    }

    public get type(): UserType | undefined {
        return this._type;
    }

    public get access_controls(): any[] | undefined {
        return this._access_controls;
    }

    /**
     * Retrieves the unique permissions for the user across all their roles.
     * @returns An array of unique permission strings.
     * @example
     * const permissions = user.appPermissions();
     * console.log(permissions);
     */
    public get appPermissions(): string[] {
        return this.getUniquePermissions();
    }

    /**
     * Determines the user's avatar color based on the first letter of their last name.
     * @returns The avatar color as a string.
     * @example
     * const color = user.avatarColor;
     * console.log(color); // "red"
     */
    public get avatarColor(): string {
        return User.avatarColor(this._last_name);
    }

    /**
     * Converts the `User` instance to a plain JSON object.
     *
     * This method overrides the base `toJSON` implementation to include user-specific fields, such as `username`,
     * `first_name`, `last_name`, and associated `roles`.
     *
     * @override
     * @returns An object containing the `User`'s fields and associated roles.
     *
     * @example
     * ```ts
     * const user = new User("123", "johndoe", "John", "Doe", "partner123", "icon.png", "john.doe@example.com", "+123456789", new Date(), new Date(), new Date(), true, []);
     * console.log(user.toJSON());
     * // {
     * //   id: "123",
     * //   username: "johndoe",
     * //   first_name: "John",
     * //   last_name: "Doe",
     * //   partner_id: "partner123",
     * //   user_icon: "icon.png",
     * //   email: "john.doe@example.com",
     * //   phone: "+123456789",
     * //   created_at: "2024-01-01T00:00:00.000Z",
     * //   last_login: "2024-01-02T00:00:00.000Z",
     * //   updated_at: "2024-01-03T00:00:00.000Z"
     * //   isAdmin: true,
     * //   roles: []
     * // }
     * ```
     */
    public override toJSON() {
        return {
            id: this.id,
            username: this._username,
            first_name: this._first_name,
            last_name: this._last_name,
            partner_id: this._partner_id,
            user_icon: this._user_icon,
            email: this._email,
            phone: this._phone,
            created_at: this._created_at,
            last_login: this._last_login,
            isAdmin: this._isAdmin,
            roles: this._roles,
            user_status: this._user_status,
            two_factor_enabled: this._two_factor_enabled,
            updated_at: this._updated_at,
            type: this._type,
            permissions: this._access_controls,
        }
    }

    /**
     * Retrieves the unique permissions for the user.
     * Utilizes a `Set` to ensure no duplicate permissions.
     * @returns An array of unique permission strings.
     */
    private getUniquePermissions(): string[] {
        const permissionSet = new Set<string>();

        return this.roles
            .flatMap((role) => role.permissions) // Flatten permissions from all roles
            .filter((permission) => {
                const permissionString = permission.permissionString;
                if (!permissionSet.has(permissionString)) {
                    permissionSet.add(permissionString);
                    return true;
                }
                return false;
            })
            .map((permission) => permission.permissionString);
    }

    public async update(values: Partial<IUserForm>): Promise<[boolean, User, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.put(`/api/v4/users/${this.id}`, values, {
                withCredentials: true,
                headers: {
                    Authorization: `Bearer ${tokenCookie}`,
                }
            })

            this._username = response.data.user.username;
            this._first_name = response.data.user.first_name;
            this._last_name = response.data.user.last_name;
            this._partner_id = response.data.user.partner_id;
            this._user_icon = response.data.user.user_icon;
            this._email = response.data.user.email;
            this._phone = response.data.user.phone;
            this._created_at = response.data.user.created_at;
            this._last_login = response.data.user.last_login;
            this._isAdmin = response.data.user.isAdmin;
            this._roles = response.data.user.roles?.map(
                (role) => new AppRole(role.id, role.title, role.permissions, role.users)
            );
            this._user_status = response.data.user.user_status;
            this._two_factor_enabled = response.data.user.two_factor_enabled;
            this._updated_at = response.data.user.updated_at;
            this._type = response.data.user.type;
            this._access_controls = response.data.user?.access_controls;

            return [true, this];
        } catch (error) {
            return [false, null, error];
        }
    }

    public async updateProfilePicture(formData: FormData): Promise<[boolean, User, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.post(`/api/v4/users/${this.id}/photo`, formData, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${tokenCookie}`,
                }
            })

            this._user_icon = response.data.url;
            return [true, this];
        } catch (error){
            return [false, null, error];
        }
    }

    public async destroy(): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            await api.delete(`/api/v4/users/${this.id}`, {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${tokenCookie}`,
                },
            })

            return [true];
        } catch (error) {
            return [false, null];
        }
    }

    public async activate(): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            await api.post(`/api/v4/users/${this.id}/activate`, {}, {
                withCredentials: true,
                headers: {
                    Authorization: `Bearer ${tokenCookie}`,
                }
            })

            return [true];
        } catch (error) {
            return [false, error];
        }
    }

    public async deactivate(emergency: boolean = false): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            await api.post(`/api/v4/users/${this.id}/deactivate`, {
                emergency,
            }, {
                withCredentials: true,
                headers: {
                    Authorization: `Bearer ${tokenCookie}`,
                }
            })

            return [true];
        } catch (error) {
            return [false, error];
        }
    }

    public async disableTwoFactor(): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");
        try {
            await api.patch(`/api/v4/users/${this.id}/tfa-toggle`, {
                toggle: false,
            }, {
                withCredentials: true,
                headers: {
                    Authorization: `Bearer ${tokenCookie}`,
                }
            })

            this._two_factor_enabled = false;
            return [true];
        } catch (error) {
            return [false, error];
        }
    }

    public async enableTwoFactor(): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");
        try {
            await api.patch(`/api/v4/users/${this.id}/tfa-toggle`, {
                toggle: true,
            }, {
                withCredentials: true,
                headers: {
                    Authorization: `Bearer ${tokenCookie}`,
                }
            })

            this._two_factor_enabled = true;
            return [true];
        } catch (error) {
            return [false, error];
        }
    }
}

export default User;

export interface IUserForm {
    first_name: string
    last_name: string
    email: string
    phone: string
    username: string
    user_status: IUserStatus
    type: UserType
    isAdmin: 0 | 1
    partner_id: string,
}